/**
 * AnimatedPortfolioValue - Robinhood-style animated portfolio value display
 *
 * Features:
 * - Counting animation on load and value changes
 * - Color transitions based on P&L
 * - Scrubbing state display with timestamp
 * - Smooth easing animations with Framer Motion
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useVault } from '../../contexts/VaultContext';
import { useFeedback } from '../../contexts/FeedbackContext';

interface AnimatedPortfolioValueProps {
  className?: string;
}

const AnimatedPortfolioValue: React.FC<AnimatedPortfolioValueProps> = ({ className = '' }) => {
  const {
    currentValue,
    displayValue,
    change24h,
    change24hPercent,
    isScrubbing,
    scrubValue,
    scrubTimestamp,
    chartChange,
    chartChangePercent,
    selectedTimeframe,
    isLoading,
  } = useVault();

  const { playSound, soundEnabled } = useFeedback();

  // Animation state
  const [animatedValue, setAnimatedValue] = useState(0);
  const [isCountingUp, setIsCountingUp] = useState(false);
  const previousValueRef = useRef(0);
  const animationRef = useRef<number | null>(null);
  const soundIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Format currency value
  const formatCurrency = useCallback((value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }, []);

  // Format percentage
  const formatPercent = useCallback((value: number): string => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  }, []);

  // Format change value
  const formatChange = useCallback((value: number): string => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${formatCurrency(value).replace('$', '')}`;
  }, [formatCurrency]);

  // Format timestamp for scrubbing
  const formatTimestamp = useCallback((timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }, []);

  // Animate counting effect
  useEffect(() => {
    if (isLoading) return;

    const targetValue = isScrubbing && scrubValue !== null ? scrubValue : displayValue;
    const startValue = previousValueRef.current;

    // Skip animation if values are the same or scrubbing
    if (startValue === targetValue || isScrubbing) {
      setAnimatedValue(targetValue);
      previousValueRef.current = targetValue;
      return;
    }

    setIsCountingUp(targetValue > startValue);

    const duration = 1500; // 1.5 seconds
    const startTime = performance.now();

    // Play counting sounds at intervals
    if (soundEnabled && Math.abs(targetValue - startValue) > 10) {
      const soundCount = Math.min(8, Math.ceil(Math.abs(targetValue - startValue) / 100));
      const interval = duration / soundCount;

      soundIntervalRef.current = setInterval(() => {
        playSound('tick');
      }, interval);

      setTimeout(() => {
        if (soundIntervalRef.current) {
          clearInterval(soundIntervalRef.current);
        }
      }, duration);
    }

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out cubic for satisfying deceleration
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      const currentAnimatedValue = startValue + (targetValue - startValue) * easeProgress;
      setAnimatedValue(currentAnimatedValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        previousValueRef.current = targetValue;
        setIsCountingUp(false);
      }
    };

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (soundIntervalRef.current) {
        clearInterval(soundIntervalRef.current);
      }
    };
  }, [displayValue, isLoading, isScrubbing, scrubValue, soundEnabled, playSound]);

  // Update animated value immediately when scrubbing
  useEffect(() => {
    if (isScrubbing && scrubValue !== null) {
      setAnimatedValue(scrubValue);
    }
  }, [isScrubbing, scrubValue]);

  // Determine color based on change
  const getChangeColor = useCallback((change: number): string => {
    if (change > 0) return 'text-green-400';
    if (change < 0) return 'text-red-400';
    return 'text-gray-400';
  }, []);

  // Get background gradient based on performance
  const getBackgroundGradient = useCallback((change: number): string => {
    if (change > 0) return 'from-green-500/5 to-transparent';
    if (change < 0) return 'from-red-500/5 to-transparent';
    return 'from-gray-500/5 to-transparent';
  }, []);

  // Get the relevant change values based on context
  // When scrubbing or viewing a non-24h timeframe, use the chart-based change values
  // Only use change24h when viewing the 1D (Today) timeframe and not scrubbing
  const useChartChange = isScrubbing || selectedTimeframe !== '1D';
  const relevantChange = useChartChange ? chartChange : change24h;
  const relevantChangePercent = useChartChange ? chartChangePercent : change24hPercent;

  // Get timeframe label
  const getTimeframeLabel = useCallback((timeframe: string): string => {
    const labels: Record<string, string> = {
      '1D': 'Today',
      '1W': 'Past Week',
      '1M': 'Past Month',
      '3M': 'Past 3 Months',
      '1Y': 'Past Year',
      'ALL': 'All Time',
    };
    return labels[timeframe] || timeframe;
  }, []);

  if (isLoading) {
    return (
      <div className={`${className} animate-pulse`}>
        <div className="h-12 bg-gray-700 rounded-lg w-48 mb-2" />
        <div className="h-6 bg-gray-700 rounded w-32" />
      </div>
    );
  }

  return (
    <div className={`${className} relative`}>
      {/* Background gradient based on performance */}
      <motion.div
        className={`absolute inset-0 bg-gradient-to-b ${getBackgroundGradient(relevantChange)} rounded-lg -z-10`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      />

      {/* Main value */}
      <motion.div
        className="text-5xl md:text-6xl font-bold tracking-tight"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <motion.span
          className={`${isCountingUp ? 'text-green-400' : ''} transition-colors duration-300`}
          animate={{
            scale: isCountingUp ? [1, 1.02, 1] : 1,
          }}
          transition={{ duration: 0.3 }}
        >
          {formatCurrency(animatedValue)}
        </motion.span>
      </motion.div>

      {/* Change indicator */}
      <motion.div
        className="flex items-center gap-2 mt-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        {/* Change amount */}
        <span className={`text-lg font-medium ${getChangeColor(relevantChange)}`}>
          {formatChange(relevantChange)}
        </span>

        {/* Change percentage */}
        <span className={`text-lg font-medium ${getChangeColor(relevantChangePercent)}`}>
          ({formatPercent(relevantChangePercent)})
        </span>

        {/* Timeframe label or scrub timestamp */}
        <AnimatePresence mode="wait">
          {isScrubbing && scrubTimestamp ? (
            <motion.span
              key="scrub"
              className="text-sm text-gray-400 ml-2"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
            >
              {formatTimestamp(scrubTimestamp)}
            </motion.span>
          ) : (
            <motion.span
              key="timeframe"
              className="text-sm text-gray-500 ml-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {getTimeframeLabel(selectedTimeframe)}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Scrubbing indicator */}
      <AnimatePresence>
        {isScrubbing && (
          <motion.div
            className="absolute -top-1 -right-1"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
          >
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AnimatedPortfolioValue;
